/**
 * Number formatting. One place, used everywhere.
 *
 * Suffix notation runs K through Qi (1e3 .. 1e18); at 1e21 and above, or when
 * the player has chosen scientific, values switch to mantissa-and-exponent.
 * Everything renders with tabular figures so digits never shift the layout.
 */

import type Decimal from 'break_infinity.js';
import type { NumberFormat } from '../sim/state.ts';

const SUFFIXES = ['', 'K', 'M', 'B', 'T', 'Qa', 'Qi'] as const;

/** Exponent at which we run out of suffixes and fall back to scientific. */
const SCIENTIFIC_EXPONENT = (SUFFIXES.length - 1) * 3 + 3; // 21

export function formatNumber(value: Decimal, mode: NumberFormat = 'standard'): string {
  if (!Number.isFinite(value.mantissa) || !Number.isFinite(value.exponent)) return '∞';
  if (value.eq(0)) return '0';
  if (value.lt(0)) return '-' + formatPositive(value.neg(), mode);
  return formatPositive(value, mode);
}

export function formatRate(value: Decimal, mode: NumberFormat = 'standard'): string {
  return formatNumber(value, mode) + ' / s';
}

/** Whole counts that never grow large: owned units, ranks, meltdowns. */
export function formatInt(value: number): string {
  return Math.floor(value).toLocaleString('en-US');
}

/** Seconds as m:ss, or h:mm:ss past an hour. */
export function formatDuration(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  const s = total % 60;
  const m = Math.floor(total / 60) % 60;
  const h = Math.floor(total / 3600);
  const ss = String(s).padStart(2, '0');
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${ss}`;
  return `${m}:${ss}`;
}

// ---------------------------------------------------------------------------

function formatPositive(value: Decimal, mode: NumberFormat): string {
  const exp = value.exponent;

  if (mode === 'scientific' || exp >= SCIENTIFIC_EXPONENT) return scientific(value);
  if (exp < 3) return small(value.toNumber());

  let tier = Math.floor(exp / 3);
  let scaled = value.mantissa * Math.pow(10, exp - tier * 3);
  let text = threeSigFigs(scaled);

  // Rounding can push 999.6 up to "1000"; promote it to the next suffix.
  if (parseFloat(text) >= 1000) {
    tier += 1;
    scaled /= 1000;
    text = threeSigFigs(scaled);
  }

  const suffix = SUFFIXES[tier];
  if (suffix === undefined) return scientific(value);
  return `${text} ${suffix}`;
}

/** Below 1000: up to two decimals, trailing zeros trimmed. */
function small(n: number): string {
  let text = trimZeros(n.toFixed(2));
  if (parseFloat(text) === 0 && n > 0) {
    // Too small for two decimals - show two significant figures instead.
    text = trimZeros(n.toPrecision(2));
  }
  return text;
}

/** Keeps trailing zeros: stable width matters more than tidiness here. */
function threeSigFigs(n: number): string {
  if (n < 10) return n.toFixed(2);
  if (n < 100) return n.toFixed(1);
  return n.toFixed(0);
}

function scientific(value: Decimal): string {
  return `${value.mantissa.toFixed(2)}e${value.exponent}`;
}

function trimZeros(text: string): string {
  if (!text.includes('.')) return text;
  return text.replace(/\.?0+$/, '');
}
